import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Card, Button, Table, Typography, Alert, Space, Modal, Spin, Result,
} from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import EncaissementImputationPicker from '../../components/EncaissementImputationPicker';
import {
    getCouvertureSinistre,
    getLegacyPaiements,
    soumettreReconciliation,
} from '../../variables/api/imputationService';
import { formatMontant } from '../../variables/constants';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export default function ReconciliationImputations() {
    const { t } = useTranslation('admin');
    const { sinistreId } = useParams();
    const navigate = useNavigate();
    const [imputationsParPaiement, setImputationsParPaiement] = useState({});
    const [completionParPaiement, setCompletionParPaiement] = useState({});

    const { data: couverture, isLoading: loadingCouverture } = useQuery({
        queryKey: ['couverture-sinistre', sinistreId],
        queryFn: () => getCouvertureSinistre(sinistreId),
        enabled: !!sinistreId,
    });

    const { data: legacyPaiements, isLoading: loadingLegacy, refetch } = useQuery({
        queryKey: ['legacy-paiements', sinistreId],
        queryFn: () => getLegacyPaiements(sinistreId),
        enabled: !!sinistreId,
    });

    const mutation = useMutation({
        mutationFn: (payload) => soumettreReconciliation(sinistreId, payload),
        onSuccess: (count) => {
            toast.success(t('reconciliation.messages.imputationsCreees', { n: count }));
            setImputationsParPaiement({});
            setCompletionParPaiement({});
            refetch();
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || t('reconciliation.messages.erreur'));
        },
    });

    const allComplete = useMemo(() => {
        if (!legacyPaiements || legacyPaiements.length === 0) return false;
        return legacyPaiements.every((p) => completionParPaiement[p.paiementTrackingId]);
    }, [legacyPaiements, completionParPaiement]);

    const handleImputationChange = (paiementTrackingId) => ({ imputations, isComplete }) => {
        setImputationsParPaiement((prev) => ({
            ...prev,
            [paiementTrackingId]: imputations,
        }));
        setCompletionParPaiement((prev) => ({
            ...prev,
            [paiementTrackingId]: isComplete,
        }));
    };

    const handleSubmit = () => {
        const payload = legacyPaiements
            .filter((p) => completionParPaiement[p.paiementTrackingId])
            .map((p) => ({
                paiementTrackingId: p.paiementTrackingId,
                imputations: imputationsParPaiement[p.paiementTrackingId] || [],
            }));
        if (payload.length === 0) {
            toast.warn(t('reconciliation.messages.aucunPaiementComplet'));
            return;
        }
        Modal.confirm({
            title: t('reconciliation.confirmation.titre'),
            content: t('reconciliation.confirmation.contenu', { n: payload.length }),
            okText: t('reconciliation.confirmation.confirmer'),
            cancelText: t('reconciliation.confirmation.annuler'),
            onOk: () => mutation.mutate(payload),
        });
    };

    if (loadingCouverture || loadingLegacy) {
        return (
            <div style={{ padding: 48, textAlign: 'center' }}>
                <Spin tip={t('reconciliation.chargement')} />
            </div>
        );
    }

    if (!sinistreId) {
        return <Result status="warning" title={t('reconciliation.sinistreNonSpecifie')} />;
    }

    const noEncaissement = !couverture?.encaissements?.length;

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Button onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
                        {t('reconciliation.retour')}
                    </Button>
                    <Title level={3}>{t('reconciliation.titre')}</Title>
                    <Text type="secondary">
                        {t('reconciliation.sinistre')} <code>{sinistreId}</code>
                    </Text>
                </div>

                {/* Bandeau couverture */}
                <Card size="small" title={t('reconciliation.couverture.titre')}>
                    <Space size="large" wrap>
                        <Text>{t('reconciliation.couverture.encaisseActif')} <strong>{formatMontant(couverture?.totalEncaisseActif)}</strong></Text>
                        <Text>{t('reconciliation.couverture.prefiActif')} <strong>{formatMontant(couverture?.totalPrefiActif)}</strong></Text>
                        <Text>{t('reconciliation.couverture.engages')} <strong>{formatMontant(couverture?.totalPaiementsActifs)}</strong></Text>
                        <Text style={{ color: Number(couverture?.soldeNet) >= 0 ? '#1a6b3c' : '#dc2626' }}>
                            {t('reconciliation.couverture.soldeNet')} <strong>{formatMontant(couverture?.soldeNet)}</strong>
                        </Text>
                    </Space>
                </Card>

                {noEncaissement && (
                    <Alert
                        type="warning"
                        showIcon
                        message={t('reconciliation.aucunEncaissement')}
                    />
                )}

                {!loadingLegacy && legacyPaiements?.length === 0 && (
                    <Result
                        status="success"
                        title={t('reconciliation.aucunPaiement.titre')}
                        subTitle={t('reconciliation.aucunPaiement.description')}
                    />
                )}

                {legacyPaiements?.length > 0 && !noEncaissement && (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            message={t('reconciliation.paiementsLegacy.titre', { n: legacyPaiements.length })}
                            description={t('reconciliation.paiementsLegacy.description')}
                        />

                        {legacyPaiements.map((p) => (
                            <Card
                                key={p.paiementTrackingId}
                                title={
                                    <Space>
                                        <Text strong>{p.numeroPaiement || p.paiementTrackingId.slice(0, 8)}</Text>
                                        <Text type="secondary">- {p.beneficiaire || '-'}</Text>
                                        <Text>{formatMontant(p.montant)}</Text>
                                        {completionParPaiement[p.paiementTrackingId] && (
                                            <Text style={{ color: '#1a6b3c' }}>{t('reconciliation.complet')}</Text>
                                        )}
                                    </Space>
                                }
                            >
                                <EncaissementImputationPicker
                                    sinistreTrackingId={sinistreId}
                                    montantCible={Number(p.montant)}
                                    onChange={handleImputationChange(p.paiementTrackingId)}
                                />
                            </Card>
                        ))}

                        <div style={{ textAlign: 'right' }}>
                            <Button
                                type="primary"
                                size="large"
                                disabled={!allComplete || mutation.isPending}
                                loading={mutation.isPending}
                                onClick={handleSubmit}
                                style={{ background: '#059669', borderColor: '#059669' }}
                            >
                                {t('reconciliation.soumettre')}
                            </Button>
                            {!allComplete && (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">
                                        {t('reconciliation.tousImputes')}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Space>
        </div>
    );
}
